import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { Button, Drawer } from '../../../../../../../ui';
import { generateReportPDF, getReport } from '../../../../../../../utils/api_service/SmartInterview';
import LoadingSpinner from '../../../../ui/LoaderSpinner';

type GenerateReportProps = {
    isOpen: boolean,
    onCloseDrawer: () => void,
    userIds: string[],
    interviewRoundId: string | undefined
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export const GenerateReport = ({
  isOpen,
  onCloseDrawer,
  userIds,
  interviewRoundId,
  loading,
  setLoading,
}: GenerateReportProps) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;

  // English keys sent to API — do not translate these
  const columnLabels = [
    'Summary',
    'Question and Answer',
    'Transcript',
    'Technical Skills',
    'Industry Skills',
    'Product Knowledge',
    'Communication',
    'Problem Solving',
    'Leadership',
    'Team Collaboration',
    'Suggested Questions',
    'Interview Type',
  ];

  // Display labels mapped from English keys
  const columnLabelTranslations: Record<string, string> = {
    'Summary': t(SI.results.report.sections.summary),
    'Question and Answer': t(SI.results.report.sections.questionAndAnswer),
    'Transcript': t(SI.results.report.sections.transcript),
    'Technical Skills': t(SI.results.report.sections.technicalSkills),
    'Industry Skills': t(SI.results.report.sections.industrySkills),
    'Product Knowledge': t(SI.results.report.sections.productKnowledge),
    'Communication': t(SI.results.report.sections.communication),
    'Problem Solving': t(SI.results.report.sections.problemSolving),
    'Leadership': t(SI.results.report.sections.leadership),
    'Team Collaboration': t(SI.results.report.sections.teamCollaboration),
    'Suggested Questions': t(SI.results.report.sections.suggestedQuestions),
    'Interview Type': t(SI.results.report.sections.interviewType),
  };

    // ✅ Initialize checked state for each item
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(
    columnLabels.reduce((acc, label) => ({ ...acc, [label]: true }), {}),
  );

  const [reportId, setReportId] = useState<string>();
  const [pdfURL, setPdfURL] = useState<string>();

  // ✅ Toggle checkbox value
  const handleCheckboxChange = (label: string) => {
    setCheckedItems((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  useEffect(() => {
    setLoading(true);
    const poll = async () => {
      if (!interviewRoundId || !reportId){
        setLoading(false);
        return;
      }

      try {
        const response = await getReport(interviewRoundId, reportId);
        const responseData = await response.json();

        if (response.ok) {
          if (responseData.status === 'SUCCESS') {
            toast.success(t(SI.results.report.toasts.generated), {
              position: 'bottom-center',
            });
            setPdfURL(responseData.pdfFilename);
            setLoading(false);
            return;
          }

          setTimeout(poll, 4000); // Retry after delay
        } else {
          toast.error(t(SI.results.report.toasts.failedStatus), {
            position: 'bottom-center',
          });
          setLoading(true);
        }
      } catch (err) {
        setTimeout(poll, 4000);
      }
    };

    poll(); // Start polling
  }, [interviewRoundId, reportId]);

  const handleGenerate = async () => {
    if (!interviewRoundId) {
      toast.error(t(SI.results.report.toasts.missingRoundId), {
        position: 'bottom-center',
      });
      return;
    }

    const selectedLabels = Object.entries(checkedItems)
      .filter(([_, checked]) => checked)
      .map(([label]) => label);

    try {
      const response = await generateReportPDF(userIds, selectedLabels, interviewRoundId);
      const responseData = await response.json();

      if (response.ok) {
        setReportId(responseData.id);
      } else {
        toast.success(t(SI.results.report.toasts.generated), {
          position: 'bottom-center',
        });
        setLoading(false);
      }
    } catch (err) {
      toast.error(t(SI.results.report.toasts.failedGenerate), {
        position: 'bottom-center',
      });
    }
  };

  return (
    <Drawer
        isOpen={isOpen}
        onClose={onCloseDrawer}
        footerContent={
          <div className='flex justify-end gap-5 w-full'>
            <Button label={t(SI.results.report.generate)} onClick={handleGenerate} variant='primary'/>
            <Button label={t(SI.results.report.close)} onClick={onCloseDrawer} variant='secondary'/>
          </div>
        }
        title={t(SI.results.report.title)}
        width='w-full md:w-2/5'
    >
      <div className='px-6 pt-6 pb-2'>
        <h2 className='text-lg font-semibold text-gray-800'>
          { t(SI.results.report.heading) }
        </h2>
        <p className='text-sm text-gray-500 mt-1'>
          { t(SI.results.report.subtitle) }
        </p>
      </div>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-5 p-6'>
        { columnLabels.map((label) => (
          <label
              key={label}
              className='flex items-center gap-2 p-2 border rounded font-medium text-primary-text cursor-pointer'
          >
            <input
                type='checkbox'
                checked={checkedItems[label]}
                onChange={() => handleCheckboxChange(label)}
                className='accent-primary-main'
            />
            { columnLabelTranslations[label] ?? label }
          </label>
        )) }
      </div>
      { loading ? (
        <div className='flex justify-center items-center min-h-[15vh]'>
          <LoadingSpinner label='' />
        </div>
      ) : (
        pdfURL && (
          <a
              href={pdfURL}
              download
              target='_blank'
              rel='noopener noreferrer'
              className='text-blue-600 underline'
          >
            { t(SI.results.report.download) }
          </a>
        )
      ) }
    </Drawer>
  );
};
